/**
 * `AuftragEckdatenKarte` — WP3a (Auftrags-Dashboard, Plan „Phase C.1", 08.09.2026), umgebaut in
 * PR4 (`docs/PLAN-EVENT-FELD-VERSIONIERUNG.md` Abschnitt 4.4, Mockup-Freigabe 13.09.2026). Zeigt
 * Datum/Uhrzeit/Gäste/Ort/Ort-Typ/Aufbau — editierbare Karte mit Autosave onBlur/onChange — Muster
 * `ui/VeranstaltungsDatenDrawer.tsx` (uncontrolled Inputs + `key`-Reset, PATCH direkt in der
 * Komponente, kein Sammel-Speichern-Knopf).
 *
 * **PR4 — Felder immer editierbar, kein Sperrbanner mehr:** die frühere M-08-Sperre
 * (`CONTRACT_LOCKED_FIELDS`, serverseitig bereits mit PR3 entfernt) hatte Termin/Gäste/Ort/Ort-Typ
 * ab `BOUND_STATUSES` deaktiviert. Antoines Vorgabe für die Versionierung: „Alles soll geändert
 * werden können, alles soll protokolliert werden in Versionen." Ersatz für die Sperre ist NICHT
 * mehr ein deaktiviertes Feld, sondern (a) die lückenlose Feld-Versionierung selbst
 * (`FeldVersionenDrawer`, Backend PR2/PR3) und (b) ein dezentes Banner „Geändert nach Zusage …" MIT
 * Link zum Drawer, plus ein Diff (alter Wert durchgestrichen neben neuem) direkt AM Feld, wenn es
 * NACH der ersten Zusage (`events.won_at`) tatsächlich eine Änderung gab. `fehlerText()` behält die
 * `locked_fields`/`event_locked`-Abbildung als reine Verteidigung — diese Codes sollten serverseitig
 * nicht mehr vorkommen.
 *
 * **PR4 — Adressfelder entfernt:** „Eckdaten muss eine eigene Kachel sein. Ohne Adresse. Diese
 * pflegt man in den Kundendaten." (Antoine, 13.09.2026). Der „Rechnungsadresse"-Block
 * (`eventAddress*`, Migration 350 „Auftragsadresse") ist ENTFERNT — er überschrieb ohnehin nur die
 * Kundenanschrift für die Rechnungsstellung und bleibt weiterhin über den reduzierten
 * Bearbeiten-Drawer editierbar (`event-detail.tsx`, Test-IDs `event-adresse-*`, s. Kopfkommentar
 * WP3b unten) — kein Funktionsverlust. Der „Lieferadresse"-Block (`deliveryAddress*`, Migration 385)
 * bleibt BEWUSST STEHEN: er ist ein rein logistisches Feld (wohin geliefert wird), keine
 * Kunden-/Rechnungsanschrift, und hat HIER seinen EINZIGEN Editier-Ort im gesamten Produkt — eine
 * Entfernung hätte diese Funktion ersatzlos gestrichen (Judgment Call, im Abschlussbericht der
 * PR4-Umsetzung vermerkt).
 *
 * **Eine Gästezahl je Vorgang** (Antoine-Fehlerbericht 15.10.2026, ersetzt das frühere
 * „zwei Gästezahlen, Übernahme nur per Klick“): `PATCH /api/events/:id` mit `guestCount` zieht
 * serverseitig alle Varianten mit und rechnet neu (`apps/api/src/lib/event-patch.ts`).
 * `GaestezahlAbweichungHinweis` erscheint nur noch im Restfall (Altfall oder angenommenes Angebot).
 *
 * **Übernahme-Sperre bleibt bestehen:** `OFFER_LOCKED_STATUSES` (`lib/offer-lock.ts`, ab
 * `offer_chosen`) sperrt WEITERHIN NUR die Angleichung des Angebots (der Server zieht ab dort nichts mehr mit) — das
 * ist eine andere Sperre als die entfallene Feld-Sperre (kennt kein `cancelled`) und war nie Teil
 * von `CONTRACT_LOCKED_FIELDS`.
 *
 * **Ort-Typ-Texte:** dieselben wie der Kopf-Chip (`lib/ort-typ.ts::ortTypChip`) — „Im Haus ·
 * Event" / „Außer Haus · Catering" / „Abholung". Der DB-Wert bleibt bewusst `off_site_mode =
 * 'delivery'` für „Abholung" (siehe Kopfkommentar dort) — KEINE eigene Entscheidung, das
 * umzubenennen, das ist offen bis Antoine antwortet (`aufgaben.md`).
 *
 * **Was NICHT hier ist:** Anlass/Art/Stammdaten-Ort/Angebotssprache/Rechnungsadresse bleiben im
 * reduzierten Bearbeiten-Drawer (`onOpenBearbeiten`) — diese Karte übernimmt nur die Felder, die
 * laut Plan hierher wandern. „Angebotswert" ist bewusst NICHT mehr hier (redundant mit der
 * Kennzahlen-Kopf-Kachel aus WP2, `AuftragKennzahlen`).
 *
 * **WP5c (Migration 386) — Aufbau Innen/Außen/Beides:** `setupLocation`, war NIE gesperrt (wie die
 * Adressblöcke — server-seitig bewusst NICHT in `CONTRACT_LOCKED_FIELDS`) und ist deshalb auch
 * NICHT vertragsrelevant im Sinne von PR4 (`lib/feld-versionen.ts::VERTRAGSRELEVANTE_ECKDATEN_FELDER`
 * — ein Restore auf `setupLocation` braucht daher NIE die Modal-Bestätigung aus Plan 4.3, egal wie
 * der Vorgangsstatus ist). Sichtbare deutsche Texte sind Innen/Außen/Beides (Antoine, 07.09.2026),
 * interner Wert bleibt indoor/outdoor/mixed. Dieselben drei Optionen zeigt das Feld-Widget in
 * `RueckfragenListe`, wenn `lib/rueckfrage-wirkung.ts` eine offene Rückfrage als "betrifft dieses
 * Feld" erkennt — beide Stellen schreiben über denselben `PATCH /api/events/:id`.
 *
 * **WP3b (08.09.2026, Migration 385) — Lieferadresse:** eigenständig, GETRENNT von der (seit PR4
 * entfernten) Rechnungsadresse. Editiert `deliveryAddress*`, hat HIER ihren einzigen Editier-Ort.
 * NIE gesperrt. „Kundenanschrift übernehmen" kopiert `customers.address*` mit EINEM PATCH; „Aus
 * Anfrage übernehmen" kopiert den Freitext `inquiries.details.deliveryAddress` unstrukturiert ins
 * Straßenfeld (PR #934 für eine strukturierte Aufteilung war zum Zeitpunkt der WP3b-Iteration NICHT
 * gemergt).
 */
import { useEffect, useMemo, useState, type ElementType } from "react";
import { apiMutate, useApi } from "../../hooks/useApi";
import type { ApiError } from "../../lib/api";
import { ECKDATEN_FELDER, formatiereFeldWert, formatiereZeitpunkt, type FeldVersionZeile } from "../../lib/feld-versionen";
import { LAENDER } from "../../lib/laender";
import { Card, DateInput, Icon, Input, Select, WertAenderung, ZeitEingabe, Button } from "../components";
import { AdresseFelder, type AdresseFeld, type AdresseWerte } from "../AdresseFelder";
import type { EventStatus } from "../types";
import { GaestezahlAbweichungHinweis } from "./GaestezahlAbweichungHinweis";
import { GESPERRT_HINWEIS, ROW_LABEL } from "./eckdaten-stile";
import { AenderungOffenBanner, FuerAenderungOeffnen } from "./FuerAenderungOeffnen";

/** Bildet ein `AdresseFeld` (`ui/AdresseFelder.tsx`) auf das PascalCase-Suffix ab, das
 *  `PATCH /api/events/:id` erwartet — dasselbe Schema wie `VeranstaltungsDatenDrawer.tsx`. */
function feldSuffix(feld: AdresseFeld): string {
  const suffix: Record<AdresseFeld, string> = {
    company: "Company",
    name: "Name",
    street: "Street",
    addition: "Addition",
    zip: "Zip",
    city: "City",
    countryCode: "CountryCode",
  };
  return suffix[feld];
}

/** Spiegel von `OFFER_LOCKED_STATUSES` (`apps/api/src/lib/offer-lock.ts`) — ab hier sind
 *  Live-Angebotsdaten Vertragsbestandteil, `PATCH /api/offers/:id` antwortet mit `event_locked`.
 *  Sperrt ausschließlich die Angebots-Übernahme-Aktion, NICHT mehr die Eckdaten-Felder selbst
 *  (die frühere Feld-Sperre `BOUND_STATUSES`/`CONTRACT_LOCKED_FIELDS` ist mit PR3/PR4 entfallen). */
const OFFER_LOCKED_STATUSES: readonly EventStatus[] = ["offer_chosen", "paid", "payment_failed", "balance_due", "completed"];

const LOCATION_TYPE_OPTIONS = [
  { value: "", label: "Nicht gepflegt" },
  { value: "in_house", label: "Im Haus · Event" },
  { value: "off_site", label: "Außer Haus" },
];

const OFF_SITE_MODE_OPTIONS = [
  { value: "", label: "Nicht gepflegt" },
  { value: "catering", label: "Außer Haus · Catering" },
  { value: "delivery", label: "Abholung" },
];

/** WP5c (Migration 386) — sichtbare Texte MÜSSEN mit `lib/rueckfrage-wirkung.ts::SETUP_LOCATION_OPTIONEN`
 *  übereinstimmen (Innen/Außen/Beides, Antoine 07.09.2026 - NICHT drinnen/im Freien/gemischt). */
const SETUP_LOCATION_OPTIONS = [
  { value: "", label: "Nicht gepflegt" },
  { value: "indoor", label: "Innen" },
  { value: "outdoor", label: "Außen" },
  { value: "mixed", label: "Beides" },
];

const SIDE_TITLE = "font-body-lg text-body-lg font-semibold text-on-background dark:text-inverse-on-surface";

/** Nachricht eines `ApiError` (`code` aus dem Server-Umschlag) auf einen kurzen, verstaendlichen
 *  Satz abbilden — sonst fällt der lange Server-Text 1:1 in die Karte. `locked_fields`/
 *  `event_locked` bleiben als reine VERTEIDIGUNG (PR3/PR4: sollten serverseitig fuer PATCH
 *  /api/events/:id nicht mehr vorkommen). */
function fehlerText(err: unknown): string {
  const code = (err as ApiError)?.code;
  if (code === "locked_fields" || code === "event_locked") return GESPERRT_HINWEIS;
  return err instanceof Error ? err.message : String(err);
}

export interface AuftragEckdatenZielAngebot {
  id: string;
  guestCount: number;
  /** Der berechnete Preis-Rang-Buchstabe (`offerLabelById`, `@maestro/pricing`), z. B. "A". */
  label: string;
}

export interface AuftragEckdatenKarteProps {
  eventId: string;
  status: EventStatus;
  eventDate: string | null;
  isPast: boolean;
  timeFrom: string | null;
  timeTo: string | null;
  guestCount: number | null;
  location: string | null;
  locationType: "in_house" | "off_site" | null;
  offSiteMode: "delivery" | "catering" | null;
  /** WP5c, Migration 386 — Aufbau Innen/Außen/Beides. Nie gesperrt, nie vertragsrelevant. */
  setupLocation: "indoor" | "outdoor" | "mixed" | null;
  /** Lieferadresse (Migration 385, WP3b, `events.deliveryAddress*`) — eigenstaendig, fliesst NIE
   *  in eine Rechnung ein, hat aber Vorrang vor der Rechnungsadresse fuer Wetter/Ort. Einziger
   *  verbliebener Adressblock dieser Karte (PR4 — s. Kopfkommentar). */
  deliveryAddressStreet: string | null;
  deliveryAddressZip: string | null;
  deliveryAddressCity: string | null;
  /** Migration 422 (Adressmodell, WARTESCHLANGE Punkt 7): Firma/Name/Zusatz/Land der
   *  Lieferadresse-Override — unkritisch bzgl. der Rechnungsnamen-Frage, fliesst nie in eine
   *  Rechnung ein. */
  deliveryAddressCompany: string | null;
  deliveryAddressName: string | null;
  deliveryAddressAddition: string | null;
  deliveryAddressCountryCode: string | null;
  /** Kundenanschrift — Quelle fuer „Kundenanschrift übernehmen" am Lieferadresse-Block. PR4:
   *  jetzt auch Firma/Zusatz/Land mit uebernommen (Mockup View 2: „kopiert kuenftig auch Firma/
   *  Zusatz/Land mit"). */
  customerAddressStreet: string | null;
  customerAddressZip: string | null;
  customerAddressCity: string | null;
  customerCompany: string | null;
  customerName: string | null;
  customerAddressAddition: string | null;
  customerAddressCountryCode: string | null;
  /** Freitext-Lieferadresse der Anfrage, aus der dieser Vorgang entstand
   *  (`inquiries.details.deliveryAddress`) — Quelle fuer „Aus Anfrage übernehmen". */
  inquiryDeliveryAddress: string | null;
  /**
   * A2 (13.09.2026, `docs/PLAN-VORGANGSSEITE-UMBAU.md` Einheit A): ein sich AENDERNDER Zaehler aus
   * dem Seitenkopf („Ort · ergänzen"). Jede Erhoehung klappt das Ort-Freitextfeld unten auf und
   * fokussiert es (`autoFocus` am Eingabefeld). Bewusst ein Zaehler statt eines Booleans, damit
   * ein zweiter Klick nach einem Abbrechen erneut wirkt. `0`/`undefined` = nie ausgeloest.
   */
  ortFokusToken?: number;
  currentOfferVersion: number;
  /** Die Variante, gegen die „Auch im Angebot übernehmen" geht — die verbindlich gewählte, sonst
   *  die erste (Preis-Rang A). `null` ohne aktive Variante (Ueberrname-Zeile entfaellt dann). */
  zielAngebot: AuftragEckdatenZielAngebot | null;
  /** PR4: Zeitpunkt der ERSTEN Zusage (`events.won_at`) — Grundlage fuer den Diff "geändert nach
   *  Zusage" am Feld selbst und das Banner oben. `null` vor der ersten Annahme (dann gibt es keine
   *  Zusage, gegen die verglichen werden könnte, und folgerichtig auch kein Banner). */
  wonAt: string | null;
  /** Punkt 29 P1: der Verlauf des Vorgangs — die Seite laedt ihn ohnehin (`activityQ`). Daraus
   *  leitet sich das Banner „fuer Aenderungen geoeffnet" ab, ohne neue Spalte und ohne zweiten
   *  Aufruf. Optional, damit ein Aufrufer ohne Verlauf nicht bricht: dann bleibt es still. */
  aktivitaeten?: { action: string; createdAt: string }[];
  onEventChanged: () => void;
  onOfferChanged: () => void;
  onOpenBearbeiten: () => void;
  /** PR4: öffnet den `FeldVersionenDrawer` (EINE Instanz lebt in `event-detail.tsx`, dieselbe wie
   *  die Verlaufs-Karten-Sammelzeile öffnet) — Link "Änderungen ansehen" im Banner. */
  onOpenVerlauf: () => void;
  /**
   * Vorgangsseite „Salon“ (Entscheidung E3, Antoine 26.09.2026): die Felder stehen im Drawer
   * „Eckdaten“ statt in der schmalen Seitenspalte — dort waren sie abgeschnitten („18:3(“,
   * „HH:r“). `true` = ohne Kartenrahmen und Titel (den trägt der Drawer), alle Felder vollbreit,
   * Beginn und Ende als eigene Felder. „Für Änderungen öffnen“ steht dann in der Übersichtskarte
   * der Seitenspalte (`EckdatenUebersicht`). Vorgabe `false`: unveränderte Karte.
   */
  imDrawer?: boolean;
}

export function AuftragEckdatenKarte({
  eventId,
  status,
  eventDate,
  isPast,
  timeFrom,
  timeTo,
  guestCount,
  location,
  locationType,
  offSiteMode,
  setupLocation,
  deliveryAddressStreet,
  deliveryAddressZip,
  deliveryAddressCity,
  deliveryAddressCompany,
  deliveryAddressName,
  deliveryAddressAddition,
  deliveryAddressCountryCode,
  customerAddressStreet,
  customerAddressZip,
  customerAddressCity,
  customerCompany,
  customerName,
  customerAddressAddition,
  customerAddressCountryCode,
  inquiryDeliveryAddress,
  ortFokusToken,
  currentOfferVersion,
  zielAngebot,
  wonAt,
  aktivitaeten,
  onEventChanged,
  onOfferChanged,
  onOpenBearbeiten,
  onOpenVerlauf,
  imDrawer = false,
}: AuftragEckdatenKarteProps) {
  /** Schmale Breitenbegrenzungen gelten nur in der Karte, im Drawer sind die Felder vollbreit. */
  const schmal = (klasse: string) => (imDrawer ? "" : klasse);
  /** Im Drawer ohne Kartenrahmen (den Rahmen bildet der Drawer), sonst die `Card`. */
  const Huelle: ElementType = imDrawer ? "div" : Card;
  const [fehler, setFehler] = useState<string | null>(null);
  const [gaesteUebernehmenBusy, setGaesteUebernehmenBusy] = useState(false);
  const [lieferUebernehmenBusy, setLieferUebernehmenBusy] = useState(false);
  const [lieferAusAnfrageBusy, setLieferAusAnfrageBusy] = useState(false);

  // Ort (Freitext) — Ergaenzen-Link, unveraendertes Verhalten aus der bisherigen Eckdaten-Karte
  // (event-detail.tsx `ortBearbeiten`), jetzt selbstaendig in dieser Komponente.
  const [ortBearbeiten, setOrtBearbeiten] = useState(false);
  const [ortWert, setOrtWert] = useState("");
  const [ortBusy, setOrtBusy] = useState(false);
  // A2: „Ort · ergänzen" im Seitenkopf scrollt hierher und erhoeht `ortFokusToken` — das klappt
  // das Feld auf, der Fokus kommt aus dem `autoFocus` am Eingabefeld unten. Der Startwert (0 bzw.
  // `undefined`) loest bewusst NICHTS aus, sonst stuende das Feld bei jedem Seitenaufruf offen.
  useEffect(() => {
    if (!ortFokusToken) return;
    setOrtWert("");
    setOrtBearbeiten(true);
  }, [ortFokusToken]);

  // Ort-Typ/OffSiteMode: Select ist controlled (Radix) - lokaler Zustand, der bei jedem frischen
  // Server-Wert (Reload nach PATCH oder durch eine andere Quelle, z.B. Chat) nachgezogen wird.
  const [locType, setLocType] = useState(locationType ?? "");
  useEffect(() => setLocType(locationType ?? ""), [locationType]);
  const [offMode, setOffMode] = useState(offSiteMode ?? "");
  useEffect(() => setOffMode(offSiteMode ?? ""), [offSiteMode]);
  // WP5c: dasselbe Controlled-Select-Muster wie Ort-Typ/OffSiteMode oben, aber NIE gesperrt.
  const [setupLoc, setSetupLoc] = useState(setupLocation ?? "");
  useEffect(() => setSetupLoc(setupLocation ?? ""), [setupLocation]);

  const angebotGesperrt = OFFER_LOCKED_STATUSES.includes(status);
  const zeigeVersandHinweis = status === "offer_sent";

  // PR4: Diff-Grundlage "geändert nach Zusage" — nur laden, wenn es ueberhaupt eine Zusage gibt
  // (sonst kann nichts "nach der Zusage" liegen, das Banner bleibt dann ohnehin aus).
  const versionenSeitZusageQ = useApi<FeldVersionZeile[]>(wonAt ? `/api/events/${eventId}/field-versions?limit=200` : null);
  const { wertVorZusage, juengsteAenderungNachZusage } = useMemo(() => {
    const leer = { wertVorZusage: new Map<string, unknown>(), juengsteAenderungNachZusage: null as FeldVersionZeile | null };
    if (!wonAt || !versionenSeitZusageQ.data) return leer;
    const grenze = new Date(wonAt).getTime();
    const ersteAenderungProFeld = new Map<string, { oldValue: unknown; changedAtMs: number }>();
    let neueste: FeldVersionZeile | null = null;
    for (const z of versionenSeitZusageQ.data) {
      if (!(ECKDATEN_FELDER as readonly string[]).includes(z.field)) continue;
      const t = new Date(z.changedAt).getTime();
      if (t <= grenze) continue; // NUR Aenderungen NACH der Zusage zaehlen (Plan Abschnitt 4.1)
      const bisher = ersteAenderungProFeld.get(z.field);
      if (!bisher || t < bisher.changedAtMs) ersteAenderungProFeld.set(z.field, { oldValue: z.oldValue, changedAtMs: t });
      if (!neueste || t > new Date(neueste.changedAt).getTime()) neueste = z;
    }
    const wertVorZusage = new Map<string, unknown>();
    for (const [feld, v] of ersteAenderungProFeld) wertVorZusage.set(feld, v.oldValue);
    return { wertVorZusage, juengsteAenderungNachZusage: neueste };
  }, [wonAt, versionenSeitZusageQ.data]);

  /** Diff-Zeile "alter Wert durchgestrichen neben neuem" — NUR wenn es fuer `feld` tatsaechlich
   *  eine Aenderung NACH der Zusage gab (Plan 4.1: "nur Zustand, kein Verlauf" — der Drawer traegt
   *  die volle Chronik, hier steht nur der Unterschied zu VORHER). */
  function diffAmFeld(feld: string, aktuellerWert: unknown) {
    if (!wertVorZusage.has(feld)) return null;
    return (
      <WertAenderung
        className="mb-1"
        alt={formatiereFeldWert(feld, wertVorZusage.get(feld))}
        neu={formatiereFeldWert(feld, aktuellerWert)}
      />
    );
  }

  async function patchEvent(patch: Record<string, unknown>) {
    setFehler(null);
    try {
      await apiMutate(`/api/events/${eventId}`, "PATCH", patch);
      onEventChanged();
    } catch (err) {
      setFehler(fehlerText(err));
    }
  }

  /** Ein Feld je Aufruf (Muster `VeranstaltungsDatenDrawer.tsx#patchEventAddress`) — leerer Wert
   *  wird als `null` gesendet (loeschbar). `feld` kommt aus `AdressBlock` als zusammengesetzter
   *  Feldname ("deliveryAddressStreet" usw.) - als String statt eines Union-Literaltyps, weil er
   *  sich dort per Template-Literal aus Praefix + "Street"/"Zip"/"City" ergibt. */
  async function patchAdressFeld(feld: string, wert: string) {
    await patchEvent({ [feld]: wert.trim() || null });
  }

  /** „Kundenanschrift übernehmen" — kopiert `customers.address*` mit EINEM PATCH, egal ob leer
   *  (ein einzelnes fehlendes Kundenfeld loescht dann auch das entsprechende Event-Feld, statt
   *  einen halben Adressstand stehen zu lassen). PR4 (Adressmodell): jetzt auch Firma/Name/Zusatz/
   *  Land mit — Land ist dabei das einzige Feld, das beim Kunden selbst evtl. noch fehlt
   *  (Places-Lookup liefert es nicht immer, Mockup View 2). */
  async function kundenanschriftUebernehmen() {
    setLieferUebernehmenBusy(true);
    setFehler(null);
    try {
      await apiMutate(`/api/events/${eventId}`, "PATCH", {
        deliveryAddressStreet: customerAddressStreet,
        deliveryAddressZip: customerAddressZip,
        deliveryAddressCity: customerAddressCity,
        deliveryAddressCompany: customerCompany,
        deliveryAddressName: customerName,
        deliveryAddressAddition: customerAddressAddition,
        deliveryAddressCountryCode: customerAddressCountryCode,
      });
      onEventChanged();
    } catch (err) {
      setFehler(fehlerText(err));
    } finally {
      setLieferUebernehmenBusy(false);
    }
  }

  /** „Aus Anfrage übernehmen" — kopiert den GANZEN Freitext unstrukturiert ins Straßenfeld
   *  (PLZ/Ort bleiben leer, vom Betreiber manuell nachzutragen) - eine strukturierte Aufteilung
   *  braeuchte PR #934, das zum Zeitpunkt der WP3b-Iteration nicht gemergt war. */
  async function lieferadresseAusAnfrageUebernehmen() {
    if (!inquiryDeliveryAddress?.trim()) return;
    setLieferAusAnfrageBusy(true);
    setFehler(null);
    try {
      await apiMutate(`/api/events/${eventId}`, "PATCH", { deliveryAddressStreet: inquiryDeliveryAddress.trim() });
      onEventChanged();
    } catch (err) {
      setFehler(fehlerText(err));
    } finally {
      setLieferAusAnfrageBusy(false);
    }
  }

  async function ortSpeichern() {
    if (!ortWert.trim()) return;
    setOrtBusy(true);
    try {
      await apiMutate(`/api/events/${eventId}`, "PATCH", { location: ortWert.trim() });
      setOrtBearbeiten(false);
      onEventChanged();
    } catch (err) {
      setFehler(fehlerText(err));
    } finally {
      setOrtBusy(false);
    }
  }

  async function gaesteInsAngebotUebernehmen() {
    if (!zielAngebot || guestCount == null) return;
    setGaesteUebernehmenBusy(true);
    setFehler(null);
    try {
      // Derselbe Weg wie das Gästefeld: der Server gleicht ALLE Varianten an (Altfall-Reparatur).
      await apiMutate(`/api/events/${eventId}`, "PATCH", { guestCount });
      onOfferChanged();
    } catch (err) {
      setFehler(fehlerText(err));
    } finally {
      setGaesteUebernehmenBusy(false);
    }
  }

  function onLocationTypeChange(next: string) {
    setLocType(next);
    // Wie im bisherigen Bearbeiten-Drawer-Formular (saveEdit): weg von "off_site" loescht
    // offSiteMode gleich mit - sonst bliebe ein Wert stehen, der ohne die dazugehoerige
    // Kategorie nicht mehr angezeigt werden kann.
    if (next === "off_site") {
      void patchEvent({ locationType: "off_site" });
    } else {
      setOffMode("");
      void patchEvent({ locationType: next || null, offSiteMode: null });
    }
  }

  function onOffSiteModeChange(next: string) {
    setOffMode(next);
    void patchEvent({ offSiteMode: next || null });
  }

  // WP5c: nie automatisch, nie gesperrt - reiner Feld-PATCH, exakt wie Ort-Typ/OffSiteMode oben.
  function onSetupLocationChange(next: string) {
    setSetupLoc(next);
    void patchEvent({ setupLocation: next || null });
  }

  return (
    <Huelle className={imDrawer ? "min-w-0" : "p-6"} data-testid="auftrag-eckdaten-karte">
      <div className={imDrawer ? "hidden" : "mb-4 flex items-center justify-between gap-3"}>
        <h3 className={SIDE_TITLE}>Eckdaten</h3>
        <div className="flex items-center gap-3">
          {/* WARTESCHLANGE Punkt 29 P1: der Weg aus der Sperre heraus steht dort, wo der Betreiber
              gegen sie läuft. Die Komponente zeigt sich NUR bei `offer_chosen`/`payment_failed` —
              dieselben zwei Zustände, aus denen die Route heraus öffnet. */}
          <FuerAenderungOeffnen eventId={eventId} status={status} onGeoeffnet={onEventChanged} />
          <button type="button" className="font-label-md text-[12px] text-primary hover:underline" onClick={onOpenBearbeiten}>
            Bearbeiten
          </button>
        </div>
      </div>

      {/* WARTESCHLANGE Punkt 29 P1: steht VOR dem Versandhinweis, weil es die wichtigere Lage
          beschreibt — „gesendet" stimmt auch hier, sagt aber nicht, dass eine Zusage gerade
          hinfällig geworden ist. */}
      {!imDrawer && <AenderungOffenBanner status={status} aktivitaeten={aktivitaeten ?? []} eventId={eventId} />}

      {zeigeVersandHinweis && (
        <p
          className="mb-3 rounded-lg border border-outline-variant/40 bg-surface-container-low px-3 py-2 font-body-sm text-[11.5px] text-secondary dark:border-outline/30 dark:bg-surface-variant/10 dark:text-secondary-fixed-dim"
          data-testid="eckdaten-versand-hinweis"
        >
          v{currentOfferVersion} ist gesendet — Änderung erfordert neuen Versand.
        </p>
      )}

      {/* PR4: Ersatz fuer das entfallene amber Sperrbanner — dezent, nur wenn es NACH der Zusage
          tatsaechlich eine Eckdaten-Aenderung gab (nicht: der Vorgang ist nur gebunden). */}
      {juengsteAenderungNachZusage && (
        <p
          className="mb-3 flex flex-wrap items-center gap-x-1.5 gap-y-1 rounded-lg bg-surface-container-low px-3 py-2 font-body-sm text-[11.5px] text-secondary dark:bg-surface-variant/10 dark:text-secondary-fixed-dim"
          data-testid="eckdaten-geaendert-nach-zusage-hinweis"
        >
          <Icon name="info" className="text-[14px] text-secondary-fixed-dim" />
          <span>
            Geändert nach Zusage · zuletzt <b className="text-on-background dark:text-inverse-on-surface">{formatiereZeitpunkt(juengsteAenderungNachZusage.changedAt)}</b>
            {juengsteAenderungNachZusage.actorLabel ? ` von ${juengsteAenderungNachZusage.actorLabel}` : ""}
          </span>
          <button type="button" className="ml-auto font-label-md text-[11.5px] font-semibold text-primary hover:underline" onClick={onOpenVerlauf}>
            Änderungen ansehen
          </button>
        </p>
      )}

      <div className="space-y-4">
        {/* Termin: Datum + Uhrzeit, Autosave onBlur (Muster VeranstaltungsDatenDrawer.tsx). */}
        <div className={imDrawer ? "grid grid-cols-1 gap-3 sm:grid-cols-[1.25fr_2fr]" : "grid grid-cols-1 gap-3 sm:grid-cols-2"}>
          <label className="block">
            <span className={`mb-1 block ${ROW_LABEL}`}>Datum</span>
            {diffAmFeld("eventDate", eventDate)}
            <span className="flex items-center gap-1.5">
              <DateInput
                data-testid="eckdaten-datum"
                key={`ed-${eventDate ?? ""}`}
                defaultValue={eventDate ?? ""}
                onCommit={(v) => {
                  if (!v) return;
                  void patchEvent({ eventDate: v });
                }}
              />
              {isPast && (
                <span className="whitespace-nowrap rounded-full bg-[#F0EAE0] px-1.5 py-0.5 text-[10px] font-semibold text-[#6F675A]">
                  Vergangenheit
                </span>
              )}
            </span>
          </label>
          {/* Beginn/Ende: EIN Blur-Rahmen um beide Felder (speichert erst, wenn der Fokus den Rahmen
              verlässt). Im Drawer stehen sie als zwei eigene, beschriftete Felder nebeneinander. */}
          <div className="block">
            {!imDrawer && <span className={`mb-1 block ${ROW_LABEL}`}>Uhrzeit</span>}
            {diffAmFeld("timeFrom", timeFrom)}
            <div
              className={imDrawer ? "grid grid-cols-2 gap-3" : "flex items-center gap-1.5"}
              data-testid="eckdaten-uhrzeit"
              onBlur={(e) => {
                if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
                const [von, bis] = Array.from(e.currentTarget.querySelectorAll("input")).map((el) => (el as HTMLInputElement).value);
                if (von) void patchEvent({ timeFrom: von, timeTo: bis || null });
              }}
            >
              {imDrawer ? (
                <>
                  <label className="block">
                    <span className={`mb-1 block ${ROW_LABEL}`}>Beginn</span>
                    <ZeitEingabe name="eckdatenKarteZeitVon" defaultValue={timeFrom?.slice(0, 5) ?? ""} />
                  </label>
                  <label className="block">
                    <span className={`mb-1 block ${ROW_LABEL}`}>Ende</span>
                    <ZeitEingabe name="eckdatenKarteZeitBis" defaultValue={timeTo?.slice(0, 5) ?? ""} />
                  </label>
                </>
              ) : (
                <>
                  <ZeitEingabe name="eckdatenKarteZeitVon" defaultValue={timeFrom?.slice(0, 5) ?? ""} className="max-w-[90px]" />
                  <span aria-hidden>–</span>
                  <ZeitEingabe name="eckdatenKarteZeitBis" defaultValue={timeTo?.slice(0, 5) ?? ""} className="max-w-[90px]" />
                </>
              )}
            </div>
          </div>
        </div>

        {/* Gäste (Vorgang) — PATCH /api/events/:id, der Server zieht alle Varianten mit. */}
        <label className="block">
          <span className={`mb-1 block ${ROW_LABEL}`}>Gäste (Vorgang)</span>
          {diffAmFeld("guestCount", guestCount)}
          <Input
            type="number"
            min={0}
            className={schmal("max-w-[140px]")}
            data-testid="eckdaten-gaeste-event"
            key={`eg-${guestCount ?? ""}`}
            defaultValue={guestCount ?? ""}
            onBlur={(e) => {
              if (e.target.value === "") return;
              const n = Number(e.target.value);
              if (!Number.isInteger(n) || n < 0) return setFehler("Ungültige Gästezahl – bitte eine ganze Zahl angeben.");
              void patchEvent({ guestCount: n });
            }}
          />
        </label>

        {/* Restfall: Altfall oder angenommenes Angebot, s. GaestezahlAbweichungHinweis. */}
        {zielAngebot && guestCount != null && (
          <GaestezahlAbweichungHinweis
            guestCount={guestCount}
            zielAngebot={zielAngebot}
            angebotGesperrt={angebotGesperrt}
            uebernehmenBusy={gaesteUebernehmenBusy}
            onUebernehmen={() => void gaesteInsAngebotUebernehmen()}
          />
        )}

        {/* Ort (Freitext) — im Drawer ein vollbreites Feld mit Speichern beim Verlassen (wie Datum
            und Gäste), in der Karte das bisherige „Ergänzen“. Ab PR4 IMMER editierbar. */}
        {imDrawer ? (
          <label className="block">
            <span className={`mb-1 block ${ROW_LABEL}`}>Ort</span>
            {diffAmFeld("location", location)}
            <Input
              data-testid="eckdaten-ort-eingabe"
              key={`eo-${location ?? ""}`}
              defaultValue={location ?? ""}
              placeholder="z. B. Separée hinten, Terrasse"
              maxLength={200}
              autoFocus={Boolean(ortFokusToken)}
              onBlur={(e) => {
                const wert = e.target.value.trim();
                if (wert && wert !== (location ?? "")) void patchEvent({ location: wert });
              }}
            />
          </label>
        ) : (
        <div>
          <div className="flex items-center justify-between gap-3">
            <span className={ROW_LABEL}>Ort</span>
            <span className="font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
              {location ??
                (ortBearbeiten ? (
                  <span className="flex items-center gap-1.5">
                    <Input
                      className="!h-8 !w-40 !px-2 !py-1 text-[13px]"
                      data-testid="eckdaten-ort-eingabe"
                      aria-label="Ort"
                      autoFocus
                      value={ortWert}
                      onChange={(e) => setOrtWert(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") void ortSpeichern();
                        if (e.key === "Escape") setOrtBearbeiten(false);
                      }}
                      maxLength={200}
                    />
                    <Button type="button" variant="ghost" className="!h-7 !w-7 !p-0 text-tertiary" disabled={ortBusy} onClick={() => void ortSpeichern()} title="Speichern">
                      <Icon name="check" className="text-[16px]" />
                    </Button>
                    <Button type="button" variant="ghost" className="!h-7 !w-7 !p-0 text-secondary" onClick={() => setOrtBearbeiten(false)} title="Abbrechen">
                      <Icon name="close" className="text-[16px]" />
                    </Button>
                  </span>
                ) : (
                  <button
                    type="button"
                    className="text-primary hover:underline"
                    onClick={() => {
                      setOrtWert("");
                      setOrtBearbeiten(true);
                    }}
                  >
                    Ergänzen
                  </button>
                ))}
            </span>
          </div>
          {diffAmFeld("location", location)}
        </div>
        )}

        {/* Ort-Typ / OffSiteMode — dieselben Anzeige-Texte wie der Kopf-Chip (lib/ort-typ.ts),
            DB-Wert weiterhin 'delivery' fuer "Abholung" (offene Entscheidung, s. Kopfkommentar).
            PR4: IMMER editierbar. */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="block">
            <span className={`mb-1 block ${ROW_LABEL}`}>Ort-Typ</span>
            {diffAmFeld("locationType", locationType)}
            <Select ariaLabel="Ort-Typ" value={locType} onValueChange={onLocationTypeChange} options={LOCATION_TYPE_OPTIONS} />
          </label>
          {locType === "off_site" && (
            <label className="block">
              <span className={`mb-1 block ${ROW_LABEL}`}>Catering oder Abholung?</span>
              {diffAmFeld("offSiteMode", offSiteMode)}
              <Select ariaLabel="Catering oder Abholung" value={offMode} onValueChange={onOffSiteModeChange} options={OFF_SITE_MODE_OPTIONS} />
            </label>
          )}
        </div>

        {/* WP5c (Migration 386): Aufbau Innen/Außen/Beides — NIE gesperrt, war es auch vor PR4
            schon nicht. Nie automatisch gesetzt: nur dieser Select oder eine explizit gewählte
            Rückfragen-Antwort schreiben das Feld. */}
        <label className={`block ${schmal("max-w-[220px]")}`} data-testid="eckdaten-aufbau">
          <span className={`mb-1 block ${ROW_LABEL}`}>Aufbau</span>
          {diffAmFeld("setupLocation", setupLocation)}
          <Select ariaLabel="Aufbau" value={setupLoc} onValueChange={onSetupLocationChange} options={SETUP_LOCATION_OPTIONS} />
        </label>

        {/* PR4: NUR noch die Lieferadresse (deliveryAddress*) — die Rechnungsadresse (eventAddress*)
            ist entfallen, s. Kopfkommentar. NIE gesperrt (kein `disabled`) - server-seitig war sie
            bewusst nie in CONTRACT_LOCKED_FIELDS. */}
        <AdressBlock
          hinweis="Nur ausfüllen, wenn geliefert wird und die Lieferadresse von der Kundenanschrift abweicht. Maßgeblich für Wetter/Ort, wenn gesetzt."
          werte={{
            company: deliveryAddressCompany,
            name: deliveryAddressName,
            street: deliveryAddressStreet,
            addition: deliveryAddressAddition,
            zip: deliveryAddressZip,
            city: deliveryAddressCity,
            countryCode: deliveryAddressCountryCode,
          }}
          onFeldSpeichern={(feld, wert) => void patchAdressFeld(`deliveryAddress${feldSuffix(feld)}`, wert)}
          zeigeKundenanschriftUebernehmen={Boolean(customerAddressStreet || customerAddressZip || customerAddressCity)}
          kundenanschriftUebernehmenBusy={lieferUebernehmenBusy}
          onKundenanschriftUebernehmen={() => void kundenanschriftUebernehmen()}
          ausAnfrageText={inquiryDeliveryAddress}
          ausAnfrageBusy={lieferAusAnfrageBusy}
          onAusAnfrageUebernehmen={() => void lieferadresseAusAnfrageUebernehmen()}
        />
      </div>

      {fehler && (
        <p className="mt-3 font-body-sm text-[12px] text-error" data-testid="eckdaten-fehler">
          {fehler}
        </p>
      )}
      {imDrawer && (
        <p className="mt-5 border-t border-outline-variant/30 pt-4 font-body-sm text-[12px] text-secondary dark:border-outline/30 dark:text-secondary-fixed-dim">
          Anlass, Stammdaten-Ort und Rechnungsadresse stehen unter{" "}
          <button type="button" className="font-semibold text-primary hover:underline" onClick={onOpenBearbeiten}>
            Vorgang bearbeiten
          </button>
          .
        </p>
      )}
    </Huelle>
  );
}

/**
 * WP3b: die Lieferadresse (Autosave onBlur, dieselbe Grid-Anordnung wie
 * `VeranstaltungsDatenDrawer.tsx`/der Bearbeiten-Drawer in `event-detail.tsx`). Seit PR4 der
 * EINZIGE Adressblock dieser Karte (die Rechnungsadresse ist entfallen) — `titel`/`testId` sind
 * deshalb nicht mehr Parameter, sondern fest "Lieferadresse"/"lieferadresse". PR4 (Adressmodell,
 * WARTESCHLANGE Punkt 7): Feldset ueber die gemeinsame `AdresseFelder`-Komponente um Firma/Name/
 * Zusatz/Land erweitert (`ROW_LABEL` als Label-Klasse, dasselbe leichtere Muster wie die uebrigen
 * Felder dieser Karte, s. `labelClassName`-Prop von `AdresseFelder`).
 */
function AdressBlock({
  hinweis,
  werte,
  onFeldSpeichern,
  zeigeKundenanschriftUebernehmen,
  kundenanschriftUebernehmenBusy,
  onKundenanschriftUebernehmen,
  ausAnfrageText,
  ausAnfrageBusy,
  onAusAnfrageUebernehmen,
}: {
  hinweis: string;
  werte: AdresseWerte;
  onFeldSpeichern: (feld: AdresseFeld, wert: string) => void;
  zeigeKundenanschriftUebernehmen: boolean;
  kundenanschriftUebernehmenBusy: boolean;
  onKundenanschriftUebernehmen: () => void;
  ausAnfrageText?: string | null;
  ausAnfrageBusy?: boolean;
  onAusAnfrageUebernehmen?: () => void;
}) {
  const zeigeAusAnfrage = Boolean(ausAnfrageText?.trim()) && Boolean(onAusAnfrageUebernehmen);

  return (
    <div className="space-y-3 border-t border-outline-variant/30 pt-4 dark:border-outline/30" data-testid="eckdaten-lieferadresse">
      <div>
        <span className="font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">Lieferadresse</span>
        <p className="mt-1 font-body-sm text-[11.5px] text-secondary-fixed-dim">{hinweis}</p>
      </div>
      <AdresseFelder
        werte={werte}
        onFeldChange={onFeldSpeichern}
        firmaName="betreiber"
        commitOn="blur"
        laender={LAENDER}
        labelClassName={ROW_LABEL}
        testIdPrefix="eckdaten-lieferadresse"
      />
      {(zeigeKundenanschriftUebernehmen || zeigeAusAnfrage) && (
        <div className="flex flex-wrap gap-3">
          {zeigeKundenanschriftUebernehmen && (
            <button
              type="button"
              className="font-label-md text-[12px] text-primary hover:underline disabled:cursor-not-allowed disabled:text-secondary"
              data-testid="eckdaten-lieferadresse-kundenanschrift-uebernehmen"
              disabled={kundenanschriftUebernehmenBusy}
              onClick={onKundenanschriftUebernehmen}
            >
              {kundenanschriftUebernehmenBusy ? "Wird übernommen …" : "Kundenanschrift übernehmen"}
            </button>
          )}
          {zeigeAusAnfrage && (
            <button
              type="button"
              className="font-label-md text-[12px] text-primary hover:underline disabled:cursor-not-allowed disabled:text-secondary"
              data-testid="eckdaten-lieferadresse-aus-anfrage-uebernehmen"
              disabled={ausAnfrageBusy}
              onClick={onAusAnfrageUebernehmen}
            >
              {ausAnfrageBusy ? "Wird übernommen …" : "Aus Anfrage übernehmen"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
